import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import Collapse from '@mui/material/Collapse';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RepeatIcon from '@mui/icons-material/Repeat';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { TOAST_CONFIG } from '../../shared/Constants';
import {
  deleteTask,
  listNotificationChannels,
  listTaskRuns,
  listTasks,
  NotificationChannel,
  runTaskNow,
  ScheduledTask,
  TaskApiError,
  TaskRun,
  updateTask,
} from '../../shared/services/scheduledTasksApi';
import DeliveryChannels, { SlackGlyph } from './DeliveryChannels';
import TaskEditModal from './TaskEditModal';
import {
  compareTasks,
  describeDelivery,
  describeRunStatus,
  describeSchedule,
  describeStatus,
  formatDateTime,
  isOneTime,
  relativeFromNow,
  shortSchedule,
  TaskGroup,
  taskGroup,
} from './taskFormat';

// ============ Layout ============

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  padding: 40px 32px 64px;
  box-sizing: border-box;
  min-height: calc(100vh - 120px);
  @media (max-width: 600px) { padding: 24px 16px 48px; }
`;

const BackButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  margin-bottom: 18px;
  align-self: flex-start;
  &:hover { color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 1rem; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 1.9rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({ theme }) => theme.font_color};
  margin: 0;
  flex: 1;
`;

const Quota = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_secondary};
  white-space: nowrap;
`;

const Lede = styled.p`
  margin: 8px 0 22px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary};
`;

// ===== Composer (cria pelo chat) =====
const Composer = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 18px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background: ${({ theme }) => theme.input_surface_bg || 'transparent'};
  transition: border-color 0.15s ease;
  &:focus-within { border-color: ${({ theme }) => theme.font_color}66; }
`;

const ComposerInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.97rem;
  padding: 8px 0;
  outline: none;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const SendBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  cursor: pointer;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  &:disabled { opacity: 0.25; cursor: default; }
  & svg { font-size: 1.15rem; }
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Suggestion = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.82rem;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  &:hover { color: ${({ theme }) => theme.font_color}; background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 0.95rem; }
`;

const Banner = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.88rem;
  line-height: 1.45;
  background: ${({ theme }) => theme.hover_overlay_light};
  color: ${({ theme }) => theme.font_color};
`;

// ===== Tabs =====
const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin: 34px 0 14px;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar_border};
`;

const Tab = styled.button<{ $on: boolean }>`
  all: unset;
  padding: 9px 12px;
  margin-bottom: -1px;
  font-size: 0.9rem;
  font-weight: ${({ $on }) => ($on ? 600 : 500)};
  cursor: pointer;
  color: ${({ $on, theme }) => ($on ? theme.font_color : theme.text_secondary)};
  border-bottom: 2px solid ${({ $on, theme }) => ($on ? theme.font_color : 'transparent')};
  &:hover { color: ${({ theme }) => theme.font_color}; }
`;

const TabCount = styled.span`
  margin-left: 6px;
  font-size: 0.78rem;
  opacity: 0.7;
`;

// ===== Cards =====
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div<{ $open: boolean }>`
  border-radius: 16px;
  border: 1px solid ${({ $open, theme }) => ($open ? `${theme.font_color}33` : theme.sidebar_border)};
  background: ${({ $open, theme }) => ($open ? theme.hover_overlay_light : 'transparent')};
  transition: background 0.15s ease, border-color 0.15s ease;
`;

const CardHead = styled.button`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  border-radius: 16px;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.font_color}55; }
`;

const KindIcon = styled.div<{ $muted: boolean }>`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.hover_overlay_light};
  color: ${({ theme }) => theme.font_color};
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  & svg { font-size: 1.25rem; }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.prompt_accent_color || theme.blue_color || '#1976d2'};
  border: 2px solid ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
`;

const CardMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 0.98rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 4px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const Sep = styled.span`
  opacity: 0.5;
`;

const DeliveryIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  & svg { font-size: 0.95rem; }
`;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
`;

type Tone = 'active' | 'running' | 'paused' | 'done' | 'failed';

const toneColor = (tone: Tone, theme: any) => {
  switch (tone) {
    case 'active':
    case 'done':
      return theme.plan_success_color || '#2e7d32';
    case 'running':
      return theme.prompt_accent_color || theme.blue_color || '#1976d2';
    case 'failed':
      return theme.error_message_text || '#d32f2f';
    default:
      return theme.text_secondary;
  }
};

const StatusPill = styled.span<{ $tone: Tone }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: ${({ $tone, theme }) => toneColor($tone, theme)};
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    ${({ $tone }) => $tone === 'running' && css`animation: ${pulse} 1.2s ease-in-out infinite;`}
  }
`;

const Chevron = styled(ExpandMoreIcon)<{ $open: boolean }>`
  flex-shrink: 0;
  color: ${({ theme }) => theme.text_secondary};
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  transition: transform 0.2s ease;
`;

// ===== Detalhe expandido =====
const Detail = styled.div`
  padding: 2px 16px 16px 70px;
  @media (max-width: 600px) { padding: 2px 14px 14px; }
`;

const Warning = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  font-size: 0.84rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.error_message_text || '#d32f2f'};
  border: 1px solid ${({ theme }) => theme.error_message_text || '#d32f2f'}44;
  & svg { font-size: 1.05rem; margin-top: 1px; }
`;

const Prompt = styled.blockquote`
  margin: 0 0 16px;
  padding: 10px 14px;
  border-left: 3px solid ${({ theme }) => theme.sidebar_border};
  font-size: 0.88rem;
  line-height: 1.55;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.font_color};
  max-height: 180px;
  overflow-y: auto;
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px 20px;
  margin: 0 0 18px;
  & dt { font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${({ theme }) => theme.text_secondary}; }
  & dd { margin: 3px 0 0; font-size: 0.88rem; color: ${({ theme }) => theme.font_color}; }
`;

const DeliveryTags = styled.dd`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  background: ${({ theme }) => theme.hover_overlay_light};
  & svg { font-size: 0.9rem; }
`;

const SubTitle = styled.div`
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
`;

const Timeline = styled.ol`
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
`;

const RunItem = styled.li<{ $tone: Tone }>`
  position: relative;
  padding: 0 0 12px 20px;
  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tone, theme }) => toneColor($tone, theme)};
  }
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 6.5px;
    top: 18px;
    bottom: 2px;
    width: 1px;
    background: ${({ theme }) => theme.sidebar_border};
  }
`;

const RunHead = styled.div`
  font-size: 0.84rem;
  color: ${({ theme }) => theme.font_color};
  & span { color: ${({ theme }) => theme.text_secondary}; }
`;

const RunBody = styled.div`
  margin-top: 3px;
  font-size: 0.82rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.text_secondary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
`;

const Muted = styled.div`
  font-size: 0.84rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionBtn = styled.button<{ $danger?: boolean; $primary?: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 9px;
  font-size: 0.84rem;
  font-weight: 550;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  color: ${({ $danger, theme }) => ($danger ? theme.error_message_text || '#d32f2f' : theme.font_color)};
  ${({ $primary, theme }) => $primary && css`
    background: ${theme.font_color};
    color: ${theme.sidebar_background_solid || theme.sidebar_background};
    border-color: ${theme.font_color};
  `}
  &:hover { opacity: 0.85; background: ${({ $primary, theme }) => ($primary ? theme.font_color : theme.hover_overlay_light)}; }
  &:disabled { opacity: 0.4; cursor: default; }
  & svg { font-size: 1rem; }
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 40px 16px;
  border-radius: 16px;
  border: 1px dashed ${({ theme }) => theme.sidebar_border};
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.9rem;
  & svg { font-size: 2rem; opacity: 0.6; margin-bottom: 4px; }
  & strong { color: ${({ theme }) => theme.font_color}; font-weight: 600; }
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 600;
  margin: 44px 0 4px;
  color: ${({ theme }) => theme.font_color};
`;

const SectionLede = styled.p`
  margin: 0 0 14px;
  font-size: 0.86rem;
  color: ${({ theme }) => theme.text_secondary};
`;

// ============ Componente ============

const SUGGESTIONS = [
  'Todo dia às 8h, me manda um resumo das notícias de IA',
  'Me lembra daqui a 30 minutos de ligar para o cliente',
  'Toda segunda às 9h, resuma as principais decisões do STF da semana',
  'Me avisa quando sair um novo edital de concurso do TCU',
];

const TAB_LABELS: Record<TaskGroup, string> = {
  active: 'Ativas',
  paused: 'Pausadas',
  done: 'Concluídas',
};

const EMPTY_TEXT: Record<TaskGroup, string> = {
  active: 'Nenhuma tarefa ativa.',
  paused: 'Nenhuma tarefa pausada.',
  done: 'Nenhum lembrete concluído ainda.',
};

const runTone = (status: string): Tone =>
  status === 'success' ? 'done' : status === 'skipped' ? 'paused' : status === 'running' ? 'running' : 'failed';

const DeliveryIcon: React.FC<{ entry: string }> = ({ entry }) => {
  const { kind, label } = describeDelivery(entry);
  if (kind === 'email') return <MailOutlineIcon titleAccess="E-mail" />;
  if (kind === 'slack') return <span title={`Slack · ${label}`} style={{ display: 'inline-flex' }}><SlackGlyph size={14} /></span>;
  return <ChatBubbleOutlineIcon titleAccess="Conversa" />;
};

interface TasksViewProps {
  onClose: () => void;
  onNavigateToChat: (chatId: string) => void;
  /** Abre já com esta tarefa expandida (ex.: clique no selo de uma mensagem). */
  focusTaskId?: string | null;
}

const TasksView: React.FC<TasksViewProps> = ({ onClose, onNavigateToChat, focusTaskId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [maxActive, setMaxActive] = useState(0);
  const [isPremium, setIsPremium] = useState(true);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TaskGroup>('active');
  const [openId, setOpenId] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, TaskRun[] | undefined>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editing, setEditing] = useState<ScheduledTask | null>(null);
  const [draft, setDraft] = useState('');

  const fetchTasks = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await listTasks(token);
      setTasks(data.tasks);
      setMaxActive(data.max_active_tasks);
      if (typeof data.is_premium === 'boolean') setIsPremium(data.is_premium);
    } catch (e) {
      console.error('Error fetching tasks:', e);
      toast.error('Não foi possível carregar as tarefas.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchChannels = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setChannels(await listNotificationChannels(token));
    } catch (e) {
      console.error('Error fetching notification channels:', e);
    }
  }, [getAccessTokenSilently]);

  const fetchRuns = useCallback(async (taskId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const list = await listTaskRuns(token, taskId);
      setRuns((prev) => ({ ...prev, [taskId]: list }));
    } catch (e) {
      setRuns((prev) => ({ ...prev, [taskId]: [] }));
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchTasks();
    fetchChannels();
  }, [fetchTasks, fetchChannels]);

  // Enquanto alguma tarefa executa, atualiza a lista (e o histórico aberto)
  // para o resultado aparecer sem recarregar a página.
  const anyRunning = tasks.some((t) => t.status === 'running');
  useEffect(() => {
    if (!anyRunning) return;
    const id = window.setInterval(() => {
      fetchTasks();
      if (openId) fetchRuns(openId);
    }, 8000);
    return () => window.clearInterval(id);
  }, [anyRunning, openId, fetchTasks, fetchRuns]);

  const grouped = useMemo(() => {
    const out: Record<TaskGroup, ScheduledTask[]> = { active: [], paused: [], done: [] };
    tasks.forEach((t) => out[taskGroup(t)].push(t));
    (Object.keys(out) as TaskGroup[]).forEach((k) => out[k].sort(compareTasks));
    return out;
  }, [tasks]);

  const toggleOpen = (task: ScheduledTask) => {
    setConfirmDeleteId(null);
    if (openId === task.task_id) {
      setOpenId(null);
      return;
    }
    setOpenId(task.task_id);
    fetchRuns(task.task_id);
    if (task.unread) {
      // Abrir conta como "lido" (otimista; o backend reconcilia no refetch).
      setTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? { ...t, unread: false } : t)));
    }
  };

  // Foco vindo de fora (selo da mensagem): troca para a aba certa e expande.
  useEffect(() => {
    if (!focusTaskId || loading) return;
    const task = tasks.find((t) => t.task_id === focusTaskId);
    if (!task) return;
    setTab(taskGroup(task));
    setOpenId(task.task_id);
    fetchRuns(task.task_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTaskId, loading]);

  const replace = (updated: ScheduledTask) =>
    setTasks((prev) => prev.map((t) => (t.task_id === updated.task_id ? updated : t)));

  const errorText = (e: unknown, fallback: string) => (e instanceof TaskApiError ? e.message : fallback);

  const handleToggleStatus = async (task: ScheduledTask) => {
    const next = task.status === 'paused' || task.status === 'failed_paused' ? 'active' : 'paused';
    setBusyId(task.task_id);
    try {
      const token = await getAccessTokenSilently();
      replace(await updateTask(token, task.task_id, { status: next }));
      toast.success(next === 'active' ? 'Tarefa retomada' : 'Tarefa pausada', { ...TOAST_CONFIG, autoClose: 2000 });
    } catch (e) {
      toast.error(errorText(e, 'Não foi possível atualizar a tarefa.'), TOAST_CONFIG);
    } finally {
      setBusyId(null);
    }
  };

  const handleRunNow = async (task: ScheduledTask) => {
    setBusyId(task.task_id);
    try {
      const token = await getAccessTokenSilently();
      await runTaskNow(token, task.task_id);
      toast.success('A tarefa vai rodar em instantes.', { ...TOAST_CONFIG, autoClose: 2500 });
      // Reflete na hora; o polling assume quando o scheduler marcar "running".
      replace({ ...task, status: 'active', next_run_at: new Date().toISOString() });
      setTab('active');
      window.setTimeout(fetchTasks, 4000);
    } catch (e) {
      toast.error(errorText(e, 'Não foi possível executar a tarefa.'), TOAST_CONFIG);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (task: ScheduledTask) => {
    setBusyId(task.task_id);
    try {
      const token = await getAccessTokenSilently();
      await deleteTask(token, task.task_id);
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      setOpenId(null);
      toast.success('Tarefa excluída', { ...TOAST_CONFIG, autoClose: 2000 });
    } catch (e) {
      toast.error(errorText(e, 'Não foi possível excluir a tarefa.'), TOAST_CONFIG);
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  const submitDraft = (text: string) => {
    const msg = text.trim();
    if (!msg) return;
    // O pedido vai para um chat novo: o modelo interpreta horário/frequência
    // e chama criar_tarefa_agendada (confirmando o que estiver ambíguo).
    window.dispatchEvent(new CustomEvent('createTaskFromPrompt', { detail: msg }));
    setDraft('');
  };

  const activeCount = grouped.active.length;
  const atCap = maxActive > 0 && activeCount >= maxActive;
  const visible = grouped[tab];

  const metaLine = (task: ScheduledTask) => {
    const parts: React.ReactNode[] = [<span key="s">{shortSchedule(task.schedule)}</span>];
    if (task.status === 'running') {
      parts.push(<span key="n">executando agora</span>);
    } else if (taskGroup(task) === 'active' && task.next_run_at) {
      parts.push(<span key="n" title={formatDateTime(task.next_run_at, task.timezone)}>próxima {relativeFromNow(task.next_run_at)}</span>);
    } else if (task.last_run_at) {
      parts.push(<span key="n" title={formatDateTime(task.last_run_at, task.timezone)}>última {relativeFromNow(task.last_run_at)}</span>);
    }
    return parts.flatMap((p, i) => (i === 0 ? [p] : [<Sep key={`sep${i}`}>·</Sep>, p]));
  };

  const renderDetail = (task: ScheduledTask) => {
    const taskRuns = runs[task.task_id];
    const paused = task.status === 'paused' || task.status === 'failed_paused';
    const busy = busyId === task.task_id;
    return (
      <Detail>
        {task.status === 'failed_paused' && (
          <Warning>
            <ErrorOutlineIcon />
            <span>
              Pausada depois de {task.consecutive_failures || 3} falhas seguidas. Revise a instrução e retome a tarefa
              {task.last_run_status ? ` (último resultado: ${describeRunStatus(task.last_run_status).toLowerCase()})` : ''}.
            </span>
          </Warning>
        )}

        <Prompt>{task.prompt}</Prompt>

        <Facts>
          <div>
            <dt>Agenda</dt>
            <dd>{describeSchedule(task.schedule)}</dd>
          </div>
          {!paused && task.next_run_at && (
            <div>
              <dt>Próxima execução</dt>
              <dd>{formatDateTime(task.next_run_at, task.timezone)}</dd>
            </div>
          )}
          <div>
            <dt>Entrega</dt>
            <DeliveryTags>
              {task.delivery.map((d) => (
                <Tag key={d}><DeliveryIcon entry={d} /> {describeDelivery(d).label}</Tag>
              ))}
            </DeliveryTags>
          </div>
          {task.model && (
            <div>
              <dt>Modelo</dt>
              <dd>{task.model}</dd>
            </div>
          )}
        </Facts>

        <SubTitle>Execuções recentes</SubTitle>
        {taskRuns === undefined ? (
          <Muted>Carregando…</Muted>
        ) : taskRuns.length === 0 ? (
          <Muted>Ainda não executou.</Muted>
        ) : (
          <Timeline>
            {taskRuns.slice(0, 5).map((r) => {
              const failedDeliveries = (r.deliveries || []).filter((d) => !d.ok);
              return (
                <RunItem key={r.started_at} $tone={runTone(r.status)}>
                  <RunHead>
                    {formatDateTime(r.started_at, task.timezone)}{' '}
                    <span>
                      · {describeRunStatus(r.status)}
                      {r.status === 'success' && (r.notified ? ' · enviado' : ' · sem novidades')}
                      {typeof r.duration_seconds === 'number' && r.duration_seconds > 0 && ` · ${Math.round(r.duration_seconds)}s`}
                    </span>
                  </RunHead>
                  {(r.error || r.message_preview) && <RunBody>{r.error || r.message_preview}</RunBody>}
                  {failedDeliveries.length > 0 && (
                    <RunBody>
                      Falha na entrega: {failedDeliveries.map((d) => describeDelivery(d.channel).label).join(', ')}
                    </RunBody>
                  )}
                </RunItem>
              );
            })}
          </Timeline>
        )}

        <Actions>
          <ActionBtn $primary onClick={() => onNavigateToChat(task.chat_id)}>
            <OpenInNewIcon /> Abrir conversa
          </ActionBtn>
          {task.status !== 'running' && (
            <ActionBtn onClick={() => handleRunNow(task)} disabled={busy}>
              <PlayArrowIcon /> Executar agora
            </ActionBtn>
          )}
          {task.status !== 'completed' && task.status !== 'running' && (
            <ActionBtn onClick={() => handleToggleStatus(task)} disabled={busy}>
              {paused ? <><PlayArrowIcon /> Retomar</> : <><PauseIcon /> Pausar</>}
            </ActionBtn>
          )}
          <ActionBtn onClick={() => setEditing(task)} disabled={busy}>
            <ModeEditOutlineIcon /> Editar
          </ActionBtn>
          {confirmDeleteId === task.task_id ? (
            <>
              <ActionBtn $danger onClick={() => handleDelete(task)} disabled={busy}>
                <DeleteOutlineIcon /> Confirmar exclusão
              </ActionBtn>
              <ActionBtn onClick={() => setConfirmDeleteId(null)}>Cancelar</ActionBtn>
            </>
          ) : (
            <ActionBtn $danger onClick={() => setConfirmDeleteId(task.task_id)} disabled={busy}>
              <DeleteOutlineIcon /> Excluir
            </ActionBtn>
          )}
        </Actions>
      </Detail>
    );
  };

  return (
    <Container>
      <BackButton onClick={onClose}>
        <ArrowBackIcon /> Voltar
      </BackButton>

      <TitleRow>
        <PageTitle>Tarefas agendadas</PageTitle>
        {maxActive > 0 && (
          <Quota>
            {activeCount} de {maxActive} {maxActive === 1 ? 'ativa' : 'ativas'}
          </Quota>
        )}
      </TitleRow>
      <Lede>
        Lembretes, resumos recorrentes e monitoramentos que o assistente executa sozinho no horário marcado —
        o resultado chega na conversa, por e-mail ou no Slack.
      </Lede>

      <Composer onSubmit={(e) => { e.preventDefault(); submitDraft(draft); }}>
        <ComposerInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Descreva a tarefa — ex.: toda sexta às 17h, resuma as principais notícias da semana sobre o agronegócio"
          aria-label="Descreva a tarefa que você quer agendar"
        />
        <SendBtn type="submit" disabled={!draft.trim()} aria-label="Criar no chat">
          <ArrowUpwardIcon />
        </SendBtn>
      </Composer>
      {tasks.length === 0 && !loading && (
        <Suggestions>
          {SUGGESTIONS.map((s) => (
            <Suggestion key={s} onClick={() => submitDraft(s)}>
              <ScheduleIcon /> {s}
            </Suggestion>
          ))}
        </Suggestions>
      )}
      {atCap && (
        <Banner>
          Você atingiu o limite de {maxActive} {maxActive === 1 ? 'tarefa ativa' : 'tarefas ativas'}. Pause ou exclua
          uma tarefa para criar outra{!isPremium && ' — ou assine o Premium para ter até 10'}.
        </Banner>
      )}

      <Tabs role="tablist">
        {(Object.keys(TAB_LABELS) as TaskGroup[]).map((g) => (
          <Tab key={g} role="tab" aria-selected={tab === g} $on={tab === g} onClick={() => setTab(g)}>
            {TAB_LABELS[g]}
            {grouped[g].length > 0 && <TabCount>{grouped[g].length}</TabCount>}
          </Tab>
        ))}
      </Tabs>

      {loading ? (
        <Muted>Carregando tarefas…</Muted>
      ) : visible.length === 0 ? (
        <Empty>
          <ScheduleIcon />
          {tasks.length === 0 ? (
            <>
              <strong>Nenhuma tarefa ainda</strong>
              <span>Peça no campo acima ou em qualquer conversa — ex.: "todo dia às 8h me manda as notícias de IA".</span>
            </>
          ) : (
            <span>{EMPTY_TEXT[tab]}</span>
          )}
        </Empty>
      ) : (
        <List>
          {visible.map((task) => {
            const open = openId === task.task_id;
            const status = describeStatus(task);
            return (
              <Card key={task.task_id} $open={open}>
                <CardHead onClick={() => toggleOpen(task)} aria-expanded={open}>
                  <KindIcon $muted={taskGroup(task) !== 'active'}>
                    {isOneTime(task.schedule) ? <NotificationsNoneIcon /> : <RepeatIcon />}
                    {task.unread && <UnreadDot title="Resultado novo" />}
                  </KindIcon>
                  <CardMain>
                    <CardTitle>{task.title}</CardTitle>
                    <CardMeta>
                      {metaLine(task)}
                      <Sep>·</Sep>
                      <DeliveryIcons>
                        {task.delivery.map((d) => <DeliveryIcon key={d} entry={d} />)}
                      </DeliveryIcons>
                    </CardMeta>
                  </CardMain>
                  <StatusPill $tone={status.tone}>{status.text}</StatusPill>
                  <Chevron $open={open} />
                </CardHead>
                <Collapse in={open} timeout={180} unmountOnExit>
                  {renderDetail(task)}
                </Collapse>
              </Card>
            );
          })}
        </List>
      )}

      <SectionTitle>Canais de entrega</SectionTitle>
      <SectionLede>Onde os resultados podem chegar. Escolha o canal ao pedir a tarefa ou ao editá-la.</SectionLede>
      <DeliveryChannels slackChannels={channels.filter((c) => c.channel === 'slack')} onChanged={fetchChannels} />

      <TaskEditModal
        task={editing}
        slackChannels={channels.filter((c) => c.channel === 'slack')}
        onClose={() => setEditing(null)}
        onSaved={(t) => {
          replace(t);
          setTab(taskGroup(t));
        }}
      />
    </Container>
  );
};

export default TasksView;
